import { FC, PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { RightSidebar } from '../../../../layout/main/rightSidebar';
import { FieldState, Form, FormRenderProps, FormSpy, Utils } from '@enterprise/common';
import { SpotButton } from '@enterprise/spot';
import { SideTab } from '../../../../components/tab/SideTab';
import { TabItem } from '../../../../components/tab/TabItem';
import {
    OverviewControl,
    PricingControl,
    TaxDiscControl,
    VariantsControl,
    HeaderControl as DatapointHeaderControl,
} from '../formsControls/datapoint';
import { AnimanaControl, HeaderControl as AnimanaHeaderControl } from '../formsControls/animana';
import { Loadable } from '../../../../store/constants.store';
import { InfoBar } from '../../components';
import { ScheduledItemSaveModal } from '../modals/ScheduledItemSaveModal';
import { Errors } from '../../../../core/enums/Errors';
import { Trans } from 'react-i18next';
import i18n from 'i18next';
import { Notifications } from '../../../Notifications';
import { InvoiceItem, InvoiceItemData, InvoiceItemWithDynamicProps, InvoiceItemWithVariantDetail, ProviderDetails } from '../../../../core/models';
import { RouterPaths } from '../../../../router';

import { InvoiceItemEditType } from '../InvoiceItemEditType.enum';
import { NavLink, useParams } from 'react-router';
import { defaultEmptyPriceBreakValues, removeEmptyPriceBreaks } from '../../../../core/helpers';
import { FormState, Mutator } from 'final-form';
import { DatasourceFullSiteInfo } from '../../../../core/models/datasource/sites';
import { useStores } from '../../../../hooks';

interface PageProps {
    action?: InvoiceItemEditType;
}

type QueryParams = {
    id: string;
    description: string;
    isSub: string;
    practice: string;
};

/** Includes async validation results returned to react-final-form `validate`. */
type FieldValidationResult = ReactNode | Promise<ReactNode | undefined>;

interface PropertyState {
    value: string;
    active: boolean;
    error?: FieldValidationResult;
}

const ItemEditPageComponent = observer(function ItemEditPageComponent(props: PageProps) {
    const {
        domain: { itemsPageStore: store, constantsStore },
        ui: { router: routerStore },
    } = useStores();

    const [activeValueName, setActiveValueName] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>();
    const [isVariantsMode, setIsVariantsMode] = useState<boolean>();
    const [data, setData] = useState({} as InvoiceItemData);
    const [updatedItems, setUpdatedItems] = useState(new Map<string, Partial<InvoiceItem>>());
    const [loadingError, setLoadingError] = useState<string>();
    const fieldsState = useRef(new Map<string, PropertyState>());
    const { invoiceItem, formType, applications, isLoadingItem, isCreatingNewItem, isLoaded, isAnimanaOnly } = store;
    const masterSiteId = invoiceItem?.difference?.['master'].provider?.siteId as string;
    const existingVariantsIds =
        [...Object.values(data)].reduce((agg, { provider }) => {
            return provider && provider.siteId !== masterSiteId ? [...agg, provider.siteId] : agg;
        }, [] as string[]) || [];
    const areFieldsDisabled = !invoiceItem || submitting || (isVariantsMode && existingVariantsIds.length === 0);
    const isLoadingConstants = constantsStore.isLoading;
    const isLoading = !isLoaded || isLoadingConstants || isLoadingItem || !invoiceItem || !activeValueName;
    const { practice: queryPractice } = routerStore.params as QueryParams;

    // Stable reference for the form's initial values. `onFormChanged` mirrors the live
    // form values back into `data`, and `data` is what seeds the form (`Form.buildForm`).
    // Passing the ever-changing `data[activeValueName]` directly made react-final-form
    // re-initialize (reset) on every change — a feedback loop that caused the form to
    // flicker. Switching variants remounts the form via `key={activeValueName}`, so the
    // form only needs a fresh reference when the active variant (or the loaded item) changes.
    // const formInitialData = useMemo(() => data[activeValueName] || data['master'], [activeValueName, invoiceItem]);

    useEffect(() => {
        constantsStore.load([Loadable.Applications]).catch(() => {
            setLoadingError(Errors.DP_REST);
        });

        return () => {
            store.clearItemState();
        };
    }, []);

    useEffect(() => {
        if (!invoiceItem?.difference) {
            return;
        }

        const data = Object.entries(invoiceItem.difference).reduce(
            (agg, [key, item]) => {
                if (key !== 'master') {
                    agg[item.provider?.siteId as string] = item;
                }

                return agg;
            },
            { master: invoiceItem.difference['master'] },
        );

        setData(cloneDeep(data));
        enableItemMode();
    }, [invoiceItem?.difference]);

    useEffect(() => {
        if (isLoadingConstants) {
            return;
        }

        const { action } = props;

        if (action === InvoiceItemEditType.New) {
            store.createInvoiceItem();
        }

        if (action === InvoiceItemEditType.Form) {
            const { id, description, isSub: subFlag, practice } = routerStore.params as QueryParams;

            if (id || description) {
                const isSub = Boolean(subFlag && JSON.parse(subFlag));
                store.fetchInvoiceItem({ id, description, practice, isSub }).catch(() => {
                    setLoadingError(Errors.DP_REST);
                });
            }
        }

        if (action !== InvoiceItemEditType.New && action !== InvoiceItemEditType.Form) {
            // Assume a legacy path was used, change the path to the new format and reload
            routerStore.replace(`${RouterPaths.ControlCenterPages.EditItem}?id=${action}`);
        }
    }, [props.action, JSON.stringify(routerStore.params), isLoadingConstants]);

    useEffect(() => {
        if (!data || !invoiceItem) {
            return;
        }

        const map = Object.keys(data).reduce((agg, key) => {
            const { variants } = (invoiceItem as InvoiceItemWithVariantDetail) || {};
            const { pimsGeneratedId, provider } = data[key];
            const variant = variants?.find((variant) => variant.pimsGeneratedId === pimsGeneratedId && variant.provider?.siteId === provider?.siteId);
            const excludedKeys = isAnimanaOnly
                ? ['providers', 'sites', 'validateOnly', 'pimsGeneratedId', 'updateDateTime']
                : ['providers', 'sites', 'validateOnly'];
            const updatedProps: Partial<InvoiceItem> = Utils.difference(variant || {}, data[key], excludedKeys);

            if (!Object.keys(updatedProps).length && key !== 'master') {
                return agg;
            }

            const updatedItem: Partial<InvoiceItem> = makeUpdateItem(data[key], cloneDeep(updatedProps));

            if (updatedProps.prices?.priceBreaks && updatedItem.prices) {
                updatedItem.prices.priceBreaks = data[key].prices?.priceBreaks;
            }

            return agg.set(key, updatedItem);
        }, new Map<string, Partial<InvoiceItem>>());

        if (!isEqual(map, updatedItems)) {
            setUpdatedItems(map);
        }
    }, [data, updatedItems, invoiceItem]);

    const makeUpdateItem = (item: Partial<InvoiceItem>, override: Partial<InvoiceItem> = {}) => {
        if (!item) {
            return item;
        }

        const { pimsGeneratedId, pimsId, clientDescription, hospitalDescription, itemType } = item;

        return {
            ...override,
            pimsGeneratedId,
            pimsId,
            clientDescription,
            hospitalDescription,
            itemType,
        };
    };

    const back = () => {
        routerStore.push('/control-center/items');
    };

    const unpackEntity = (): Partial<InvoiceItem>[] => {
        const activeSites = applications.filter((app) => app.isActive);
        return activeSites
            .reduce((agg, site) => {
                const item = getVariant(site);

                return item ? [...agg, { ...item, provider: site.provider }] : agg;
            }, [] as Partial<InvoiceItem>[])
            .map((item) => removeEmptyPriceBreaks(defaultEmptyPriceBreakValues(item)));
    };

    const getVariant = (site: DatasourceFullSiteInfo): Partial<InvoiceItem> | undefined => {
        const { isCreatingNewItem } = store;
        const { variants } = (invoiceItem as InvoiceItemWithVariantDetail) || {};
        const variant = variants?.find((variant) => variant.provider.siteId === site.provider.siteId);
        const { siteId } = site.provider;
        const item = updatedItems?.get(siteId) || updatedItems?.get('master');
        const baseItem = makeUpdateItem(data[siteId]) || updatedItems?.get('master');

        if (item?.itemParent && !item?.pimsGeneratedId) {
            return undefined;
        }

        if (updatedItems?.has(siteId)) {
            return updatedItems?.get(siteId);
        }

        if (isCreatingNewItem) {
            return updatedItems?.get('master');
        }

        if (variant && baseItem) {
            return {
                ...baseItem,
                pimsGeneratedId: variant?.pimsGeneratedId,
            };
        }

        return undefined;
    };

    const save = async (params: any, isScheduledSave = false) => {
        if ((!isScheduledSave && Boolean(params.validateOnly)) || submitting) {
            return;
        }

        setSubmitting(true);
        return onSave(params, isScheduledSave);
    };

    const onSave = async ({ name, date }: { name: string; date: Date }, isScheduledSave: boolean) => {
        setSubmitting(true);

        try {
            const items = unpackEntity();
            const message = store.getSuccessMessage(items);

            if (isScheduledSave) {
                await store.scheduleSave(items, date, message, name);
            } else {
                await store.save(items, message);
            }

            setSubmitting(false);
            back();
        } catch (error) {
            setSubmitting(false);
            throw error;
        }
    };

    const getDuplicateErrorMessage = (item: InvoiceItem, message: string, name: string): ReactNode => {
        if (!item) {
            return undefined;
        }

        const href = store.getEditUrl(item);

        return (
            <span data-automation-id="duplicate-error-message">
                {message}:{' '}
                <NavLink to={href} className={'spot-link'} target={'_blank'}>
                    {name}
                </NavLink>
            </span>
        );
    };

    const isNotExist = (value: string, meta: FieldState<unknown> | undefined, message: string): FieldValidationResult => {
        if (!value || !meta) {
            return undefined;
        }

        const { name, active } = meta;
        const prevState = fieldsState.current.get(name);

        if (!active && prevState?.active) {
            const state: PropertyState = { value, active: false, error: undefined };
            const request =
                (name === 'pimsId' && { id: value }) ||
                (name === 'hospitalDescription' && {
                    description: value,
                    matchHospitalDescription: true,
                    searchType: 'description',
                    searchValue: value,
                }) ||
                undefined;
            const errorRequest = async () => {
                if (!request) {
                    return undefined;
                }

                const existingItem = await store.fetchDuplicateInvoiceItem(request);

                if (!existingItem) {
                    return undefined;
                }
                return getDuplicateErrorMessage(existingItem, message, value);
            };

            fieldsState.current.set(name, { ...state, error: errorRequest() });
        }

        if (active && prevState?.value !== value) {
            fieldsState.current.set(name, { value, active: true, error: undefined });
        }

        return fieldsState.current.get(name)?.error;
    };

    const mutateValue: Mutator<Partial<InvoiceItem>, Partial<InvoiceItem>> = ([name], state, { changeValue }) => {
        // change the value to the same value, thus
        // triggering a revalidation of the same value
        // https://github.com/final-form/react-final-form/issues/559#issuecomment-510792155
        changeValue(state, name, (value: unknown) => value);
    };

    const variantChanged = (siteId: string) => {
        setActiveValueName(siteId);
    };

    const enableVariantsMode = () => {
        const activeVariant = existingVariantsIds[0] || 'master';

        setSubmitting(false);
        setIsVariantsMode(true);
        setActiveValueName(activeVariant);
    };

    const enableItemMode = () => {
        setSubmitting(false);
        setIsVariantsMode(false);
        setActiveValueName('master');
    };

    const addVariant = (provider: ProviderDetails, copyFrom = 'master') => {
        if (!invoiceItem) {
            return;
        }

        const copyFromKey =
            Object.keys(data).find((key) => {
                if (key === 'master' && copyFrom === 'master') {
                    return true;
                }

                try {
                    return key === copyFrom;
                } catch (e) {
                    return false;
                }
            }) || 'master';

        const copyFromExistingVariant = invoiceItem?.variants?.find(({ provider }) => provider.siteId === data[copyFromKey].provider?.siteId);
        const masterSiteId = copyFromExistingVariant?.provider.siteId || data['master'].provider?.siteId;

        const variant: Partial<InvoiceItem> = {
            ...cloneDeep(data[copyFromKey] || {}),
            provider,
            pimsGeneratedId: undefined,
            masterSiteId,
        };

        const updated = {
            ...data,
            [provider.siteId]: variant,
        };

        setData(updated);
        setActiveValueName(provider.siteId);
    };

    const patchData = (diffData: InvoiceItemData) => {
        const updated = Object.keys(diffData).reduce((agg, key) => {
            return {
                ...agg,
                [key]: {
                    ...agg[key],
                    ...diffData[key],
                },
            };
        }, data);

        setData(updated);
        variantChanged(activeValueName);
    };

    const onFormChanged = (props: FormState<Partial<InvoiceItem>>) => {
        if (!activeValueName) {
            return;
        }

        const updated = { ...data };
        const currentValues = data[activeValueName] || data['master'];

        if (isEqual(currentValues, props.values)) {
            return;
        }

        if (activeValueName !== 'master' && !data[activeValueName]) {
            const existingVariant = invoiceItem?.variants?.find((variant) => variant.provider.siteId === activeValueName);
            const variant: Partial<InvoiceItem> = {
                ...cloneDeep(props.values),
                provider: constantsStore.applications.find((site) => site.id === activeValueName)?.provider,
                pimsGeneratedId: existingVariant?.pimsGeneratedId,
                masterSiteId: data['master'].provider?.siteId,
            };

            updated[activeValueName] = variant;
        } else {
            updated[activeValueName] = props.values;
        }

        setData(updated);
    };

    const copyHospitalDescriptionToAllVariants = (description) => {
        const updated = Object.keys(data).reduce((agg, key) => {
            return {
                ...agg,
                [key]: {
                    ...agg[key],
                    hospitalDescription: description,
                    masterSiteId: data['master'].provider?.siteId,
                } as Partial<InvoiceItem>,
            };
        }, data);

        setData(updated);
        Notifications.success(i18n.t('common:item.copiedHospitalDescriptionToAllVariants'));
    };

    const getFormFields = (pristine?: boolean, invalid?: boolean, areFieldsDisabled?: boolean) => {
        if (isAnimanaOnly) {
            const itemAction = props.action;
            return (
                <div>
                    <PanelWrapper>
                        <AnimanaControl
                            store={store}
                            data={data}
                            patchData={patchData}
                            activeValueName={activeValueName}
                            disabled={areFieldsDisabled}
                            actionType={itemAction}
                        />
                    </PanelWrapper>
                </div>
            );
        }

        return (
            <SideTab data-automation-id={'item-edit-page'}>
                <TabItem label={i18n.t('common:item.info', 'Info')}>
                    <div>
                        <InfoBar title={i18n.t('common:item.info', 'Info')} isError={!pristine && invalid} />
                        <PanelWrapper>
                            <OverviewControl />
                            <PricingControl />
                        </PanelWrapper>
                    </div>
                </TabItem>
                <TabItem label={i18n.t('common:item.taxDiscounts', 'Tax & Discounts')}>
                    <div>
                        <InfoBar title={i18n.t('common:item.taxDiscounts', 'Tax & Discounts')} isError={!pristine && invalid} />
                        <PanelWrapper>
                            <TaxDiscControl />
                        </PanelWrapper>
                    </div>
                </TabItem>
            </SideTab>
        );
    };

    return (
        <>
            <RightSidebar
                isLoading={isLoading}
                loadingText={i18n.t('common:loading', 'Loading...')}
                close={back}
                modalHeaderBackground="transparent"
                modalBodyStyle={{ marginLeft: '0px', marginRight: '0px', padding: '0px' }}
            >
                <div className="item-edit-page">
                    {loadingError && <span>{loadingError}</span>}
                    {!loadingError && (
                        <>
                            <h1 style={{ padding: '12px 16px 0' }}>
                                {isCreatingNewItem ? (
                                    <Trans i18nKey="common:item.createNewItem">Create New Item</Trans>
                                ) : (
                                    <Trans i18nKey="common:item.editItem">Edit Item</Trans>
                                )}
                            </h1>
                            <div className="header">
                                <div className="variants-switcher">
                                    <a
                                        className={classNames({ active: !isVariantsMode })}
                                        onClick={enableItemMode}
                                        data-automation-id="item-details-button"
                                    >
                                        <small>
                                            <Trans i18nKey="common:item.itemDetails">Item Details</Trans>
                                        </small>
                                    </a>{' '}
                                    {!Boolean(queryPractice) && (
                                        <a
                                            className={classNames({ active: isVariantsMode })}
                                            onClick={enableVariantsMode}
                                            data-automation-id="clinic-variants-button"
                                        >
                                            <small>
                                                <Trans i18nKey="common:item.clinicVariants">Clinic Variants</Trans>
                                            </small>
                                        </a>
                                    )}
                                </div>
                            </div>

                            <Form
                                data={data[activeValueName] || data['master']}
                                key={activeValueName}
                                formType={formType}
                                onSubmit={(params) => save(params)}
                                mutators={{
                                    // potentially other mutators could be merged here
                                    mutateValue,
                                }}
                            >
                                {({ handleSubmit, invalid, pristine, form }: FormRenderProps<Partial<InvoiceItemWithDynamicProps>>) => {
                                    function submitForm() {
                                        form.change('validateOnly', false);
                                        handleSubmit();
                                    }

                                    return (
                                        <div>
                                            <fieldset disabled={areFieldsDisabled}>
                                                <FormSpy onChange={onFormChanged} />
                                                <FormSpy
                                                    subscription={{ active: true }}
                                                    // call validation on field 'active' state change
                                                    onChange={() => form.mutators.mutateValue('revalidate')}
                                                />
                                                <div className="spot-form__field-group--horizontal headerFields">
                                                    {isAnimanaOnly ? (
                                                        <AnimanaHeaderControl
                                                            areFieldsDisabled={areFieldsDisabled}
                                                            queryPractice={queryPractice}
                                                            copyDescriptionToAll={copyHospitalDescriptionToAllVariants}
                                                            isNotExist={isNotExist}
                                                        />
                                                    ) : (
                                                        <DatapointHeaderControl
                                                            areFieldsDisabled={areFieldsDisabled}
                                                            queryPractice={queryPractice}
                                                            copyDescriptionToAll={copyHospitalDescriptionToAllVariants}
                                                            isNotExist={isNotExist}
                                                        />
                                                    )}
                                                    <ScheduledItemSaveModal
                                                        form={form}
                                                        invalid={invalid}
                                                        defaultName={invoiceItem?.hospitalDescription}
                                                        onSave={(params) => save(params, true)}
                                                        title={i18n.t('controlCenter:items.scheduleItem', 'Schedule item')}
                                                    />
                                                    <SpotButton
                                                        disabled={submitting}
                                                        onClick={submitForm}
                                                        data-automation-id="item-edit-page-publish-button"
                                                    >
                                                        <Trans i18nKey="common:item.publish">Publish</Trans>
                                                    </SpotButton>
                                                </div>
                                            </fieldset>
                                            <div style={{ backgroundColor: '#fff' }}>
                                                {isVariantsMode && (
                                                    <div style={{ padding: '10px 10px 0px 10px' }}>
                                                        <VariantsControl
                                                            constants={constantsStore}
                                                            selectedVariant={activeValueName}
                                                            existingVariantsIds={existingVariantsIds}
                                                            masterSiteId={data['master'].provider?.siteId as string}
                                                            disabled={invalid}
                                                            onVariantChanged={variantChanged}
                                                            onVariantAdded={addVariant}
                                                        />
                                                        <hr />
                                                    </div>
                                                )}
                                                <fieldset disabled={areFieldsDisabled}>
                                                    {getFormFields(pristine, invalid, areFieldsDisabled)}
                                                </fieldset>
                                            </div>
                                        </div>
                                    );
                                }}
                            </Form>
                        </>
                    )}
                </div>
            </RightSidebar>
        </>
    );
});

export const ItemEditPage = () => <ItemEditPageComponent action={useParams<{ action: InvoiceItemEditType }>()?.action} />;

const PanelWrapper: FC<PropsWithChildren> = ({ children }) => <div style={{ padding: '16px' }}>{children}</div>;
